import React, { useState } from "react";
import { Check, Maximize2 } from "lucide-react";
import { useLanguage } from "../i18n";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";

export const EFFECTS = ["accento", "diffusa", "grazing", "washer", "lineare", "radente_pavimento", "atmosfera", "indiretta"];
export const OUTDOOR_EFFECTS = ["accento", "diffusa", "lineare", "indiretta", "grazing", "ambiente", "washer", "radente_pavimento", "uplight", "moonlight", "subacquea"];
const OUTDOOR_ONLY = ["ambiente", "uplight", "moonlight", "subacquea"];
export const FARLIGHT_CHOICE = "farlight";

const CAT = { residenziale: "interni", locali: "hospitality", ospitalita: "hospitality", retail: "workplace", uffici: "workplace", esterni: "esterni" };

export const LightEffectsSelect = ({ value = [], onChange, category = "", includeOutdoor = false }) => {
  const folder = CAT[category] || "interni";
  const { t } = useLanguage();
  const fx = t.effects;
  const outdoor = folder === "esterni";
  const list = outdoor ? OUTDOOR_EFFECTS : (includeOutdoor ? [...new Set([...EFFECTS, ...OUTDOOR_EFFECTS])] : EFFECTS);
  const item = (k) => fx.outdoor?.[k] || fx.items[k] || { name: k, desc: "" };
  const imgFolder = (k) => (OUTDOOR_ONLY.includes(k) ? "esterni" : folder);
  const [zoom, setZoom] = useState(null);
  const toggle = (k) => onChange(value.includes(k) ? value.filter((x) => x !== k) : [...value.filter((x) => x !== FARLIGHT_CHOICE), k]);
  const byFarlight = value.includes(FARLIGHT_CHOICE);
  return (
    <div data-testid="light-effects-select" className="space-y-3">
      <button type="button" data-testid="effect-toggle-farlight" onClick={() => onChange(byFarlight ? [] : [FARLIGHT_CHOICE])} aria-pressed={byFarlight}
        className={`w-full flex items-start gap-3 border rounded-sm p-4 text-left transition-colors ${byFarlight ? "border-ink bg-ink text-cream" : "border-[#C5A059]/60 bg-[#F7F5F0] text-ink hover:border-ink"}`}>
        <span className={`mt-0.5 w-4 h-4 shrink-0 rounded-sm border flex items-center justify-center ${byFarlight ? "bg-[#C5A059] border-[#C5A059]" : "border-ink/30"}`}>{byFarlight && <Check className="w-3 h-3 text-ink" strokeWidth={3} />}</span>
        <span><span className="block text-sm font-semibold">{fx.farlight}</span><span className={`block text-xs mt-0.5 ${byFarlight ? "text-cream/70" : "text-inksoft"}`}>{fx.farlightDesc}</span></span>
      </button>
      <div className={`grid grid-cols-2 sm:grid-cols-4 gap-3 ${byFarlight ? "opacity-50" : ""}`}>
        {list.map((k) => {
          const on = value.includes(k);
          return (
            <div key={k} data-testid={`effect-card-${k}`} className={`group relative rounded-sm overflow-hidden border-2 transition-colors ${on ? "border-amberdark" : "border-transparent hover:border-ink/30"}`}>
              <button type="button" data-testid={`effect-toggle-${k}`} onClick={() => toggle(k)} aria-pressed={on} className="block w-full text-left">
                <img key={folder} src={`/effects/${imgFolder(k)}/${k}.jpg`} alt={item(k).name} loading="lazy" className="w-full aspect-[3/2] object-cover" />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent p-3 pt-8">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-white">{item(k).name}</p>
                </div>
                {on && <span data-testid={`effect-selected-${k}`} className="absolute top-2 left-2 inline-flex items-center gap-1 bg-amberdark text-white text-[10px] font-semibold uppercase tracking-[0.15em] rounded-sm px-2 py-1"><Check className="w-3 h-3" />{fx.selected}</span>}
              </button>
              <button type="button" data-testid={`effect-zoom-${k}`} onClick={() => setZoom(k)} aria-label={fx.details} className="absolute top-2 right-2 bg-black/50 hover:bg-black/80 text-white rounded-sm p-1.5"><Maximize2 className="w-3.5 h-3.5" /></button>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-inksoft">{fx.hint}</p>
      <Dialog open={!!zoom} onOpenChange={(o) => !o && setZoom(null)}>
        <DialogContent data-testid="effect-dialog" className="bg-[#F7F5F0] text-ink border-[#C5A059]/40 rounded-sm max-w-3xl p-0 overflow-hidden">
          {zoom && (
            <>
              <img src={`/effects/${imgFolder(zoom)}/${zoom}.jpg`} alt={item(zoom).name} className="w-full aspect-[3/2] object-cover" />
              <div className="p-6 space-y-3">
                <DialogTitle className="font-display text-2xl">{item(zoom).name}</DialogTitle>
                <DialogDescription className="text-sm text-inksoft leading-relaxed">{item(zoom).desc}</DialogDescription>
                <button type="button" data-testid="effect-dialog-select" onClick={() => { toggle(zoom); setZoom(null); }} className="inline-flex items-center gap-2 bg-ink text-cream hover:bg-[#C5A059] hover:text-ink rounded-sm px-5 h-10 text-xs font-semibold uppercase tracking-[0.15em]">
                  <Check className="w-4 h-4" />{value.includes(zoom) ? fx.deselect : fx.select}
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
